import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { X, ShoppingBag, Search, User, Heart, Sun, Moon } from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";
import { useCart } from "../contexts/CartContext";
import { fetchProducts } from "../lib/api";

const NAV_H = 68; // navbar height px

export default function Navbar({ onOpenSettings }) {
  const { mode, toggleMode } = useTheme();
  const { count: cartCount, openCart } = useCart();
  const navigate = useNavigate();
  const location = useLocation();

  const [menuOpen, setMenuOpen] = useState(false);   // mobile full-screen menu
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const searchRef = useRef(null);
  const navRef = useRef(null);

  // Close everything on route change
  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
    setSearchValue("");
  }, [location.pathname]);

  // Load products for search
  useEffect(() => {
    fetchProducts({}).then((d) => setAllProducts(d || [])).catch(() => {});
  }, []);

  // Search filter
  useEffect(() => {
    const q = searchValue.trim().toLowerCase();
    if (!q) { setSearchResults([]); return; }
    setSearchResults(
      allProducts.filter((p) => {
        const hay = [p.name, p.category, p.tag || "", p.description || "", ...(p.keywords || []), ...(p.features || [])].join(" ").toLowerCase();
        return hay.includes(q);
      }).slice(0, 6)
    );
  }, [searchValue, allProducts]);

  // Escape key
  useEffect(() => {
    const fn = (e) => { if (e.key === "Escape") { setMenuOpen(false); setSearchOpen(false); } };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, []);

  const navItems = [
    { to: "/", label: "Home" },
    { to: "/shop", label: "Shop" },
    { to: "/about", label: "About Us" },
    { to: "/contact", label: "Contact" },
  ];

  const barBg = mode === "dark" ? "rgba(8,8,12,0.88)" : "rgba(255,255,255,0.85)";
  const barShadow = mode === "dark"
    ? "0 1px 0 rgba(255,255,255,0.07), 0 4px 32px rgba(0,0,0,0.6)"
    : "0 1px 0 rgba(0,0,0,0.08), 0 4px 24px rgba(0,0,0,0.06)";

  return (
    <>
      {/* ── NAVBAR BAR ─────────────────────────────────────────── */}
      <header
        ref={navRef}
        className="fixed top-0 inset-x-0 z-50"
        style={{ height: NAV_H }}
        data-testid="navbar"
      >
        {/* Rainbow bottom border */}
        <div aria-hidden="true" style={{
          position: "absolute", bottom: 0, left: 0, right: 0, height: 1.5,
          background: "linear-gradient(90deg,rgba(0,170,255,0.6),rgba(192,160,255,0.5),rgba(255,215,181,0.4),rgba(192,160,255,0.5),rgba(0,170,255,0.6))",
          opacity: 0.7,
        }} />

        <div style={{
          width: "100%", height: "100%", display: "flex", alignItems: "center",
          padding: "0 16px",
          background: barBg,
          backdropFilter: "blur(40px) saturate(200%)",
          WebkitBackdropFilter: "blur(40px) saturate(200%)",
          boxShadow: barShadow,
        }}>

          {/* ── MOBILE layout (< md) ── */}
          <div className="flex md:hidden items-center w-full">
            {/* Left: search icon — min-width matched to right group so the centered logo lands at true center */}
            <div className="flex items-center" style={{ minWidth: 76 }}>
              <button
                type="button"
                onClick={() => setSearchOpen(!searchOpen)}
                className="p-2 rounded-full flex items-center justify-center"
                aria-label="Search"
                style={{ color: "var(--mt-text)" }}
              >
                <Search size={20} />
              </button>
            </div>

            {/* Center: logo icon */}
            <Link to="/" className="flex-1 flex items-center justify-center" data-testid="nav-logo">
              <img
                src="/logo.png"
                alt="Million Threads"
                style={{
                  height: 52, width: 52, objectFit: "contain",
                  filter: mode === "dark" ? "none" : "invert(1)",
                  imageRendering: "crisp-edges",
                }}
              />
            </Link>

            {/* Right: cart + hamburger */}
            <div className="flex items-center gap-1 justify-end" style={{ minWidth: 76 }}>
              <button
                type="button"
                onClick={openCart}
                className="relative p-2 rounded-full"
                aria-label="Cart"
                style={{ color: "var(--mt-text)" }}
              >
                <ShoppingBag size={20} />
                {cartCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[17px] h-[17px] px-0.5 rounded-full flex items-center justify-center font-mono text-[0.5rem]"
                    style={{ background: "var(--mt-text)", color: "var(--mt-bg)" }}>
                    {cartCount}
                  </span>
                )}
              </button>

              <button
                type="button"
                onClick={() => setMenuOpen(true)}
                className="p-2 rounded-full flex items-center justify-center"
                aria-label="Menu"
                style={{ color: "var(--mt-text)" }}
                data-testid="nav-mobile-toggle"
              >
                {/* Clean burger — 3 lines, offset for style */}
                <svg width="22" height="16" viewBox="0 0 22 16" fill="none">
                  <line x1="2" y1="1.5" x2="20" y2="1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <line x1="6" y1="8" x2="20" y2="8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <line x1="4" y1="14.5" x2="20" y2="14.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
          </div>

          {/* ── TABLET layout (md to lg) — logo center, compact ── */}
          <div className="hidden md:flex lg:hidden items-center w-full">
            {/* Left: nav links */}
            <nav className="flex items-center gap-4 flex-shrink-0">
              {navItems.map((it) => (
                <NavLink key={it.to} to={it.to} end={it.to === "/"}
                  className={({ isActive }) => `mt-nav-link ${isActive ? "active" : ""}`}
                  style={{ fontSize: "0.72rem", letterSpacing: "0.02em", fontWeight: 400, whiteSpace: "nowrap" }}>
                  {it.label}
                </NavLink>
              ))}
            </nav>
            {/* Center: logo icon */}
            <Link to="/" className="absolute left-1/2 -translate-x-1/2 flex items-center justify-center" style={{ zIndex: 1 }} data-testid="nav-logo">
              <img
                src="/logo.png"
                alt="Million Threads"
                style={{
                  height: 44, width: 44, objectFit: "contain",
                  filter: mode === "dark" ? "none" : "invert(1)",
                  imageRendering: "crisp-edges",
                }}
              />
            </Link>
            {/* Right: search icon + cart + theme */}
            <div className="flex items-center gap-1.5 ml-auto flex-shrink-0">
              <button type="button" onClick={() => setSearchOpen(!searchOpen)} className="p-2 rounded-full" aria-label="Search">
                <Search size={18} />
              </button>
              <button type="button" onClick={openCart} className="relative p-2 rounded-full" aria-label="Cart">
                <ShoppingBag size={18} />
                {cartCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[17px] h-[17px] px-0.5 rounded-full flex items-center justify-center font-mono text-[0.5rem]"
                    style={{ background: "var(--mt-text)", color: "var(--mt-bg)" }}>{cartCount}</span>
                )}
              </button>
              <button type="button" onClick={toggleMode} className="p-2 rounded-full" aria-label="Toggle theme">
                {mode === "dark" ? <Sun size={17} /> : <Moon size={17} />}
              </button>
            </div>
          </div>

          {/* ── DESKTOP layout (≥ lg) ── */}
          <div className="hidden lg:flex items-center w-full gap-6">

            {/* Left: nav links */}
            <nav className="flex items-center gap-7 flex-shrink-0">
              {navItems.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.to === "/"}
                  className={({ isActive }) => `mt-nav-link ${isActive ? "active" : ""}`}
                  style={{ fontSize: "0.8rem", letterSpacing: "0.03em", fontWeight: 400, whiteSpace: "nowrap" }}
                >
                  {it.label}
                </NavLink>
              ))}
            </nav>

            {/* Center: brand name */}
            <Link
              to="/"
              className="absolute left-1/2 -translate-x-1/2 flex items-center justify-center"
              style={{ zIndex: 1 }}
              data-testid="nav-logo"
            >
              <span
                className="font-display font-medium mt-nav-wordmark mt-keep-anim"
                style={{ fontSize: "1rem", letterSpacing: "0.28em", whiteSpace: "nowrap" }}
              >
                MILLION THREADS
              </span>
            </Link>

            {/* Right: search + actions */}
            <div className="flex items-center gap-3 ml-auto flex-shrink-0">
              <div className="relative" ref={searchRef}>
                <div className="flex items-center gap-2 rounded-full px-4 h-9 transition-all"
                  style={{
                    background: "transparent",
                    border: `1px solid ${searchOpen || searchValue ? "rgba(0,170,255,0.4)" : "var(--mt-glass-border)"}`,
                    minWidth: 170, maxWidth: 210,
                  }}>
                  <Search size={13} style={{ color: "var(--mt-text-muted)", flexShrink: 0 }} />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    onFocus={() => setSearchOpen(true)}
                    onBlur={() => setTimeout(() => setSearchOpen(false), 200)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && searchResults.length > 0) {
                        navigate(`/product/${searchResults[0].id}`);
                        setSearchValue(""); setSearchOpen(false);
                      }
                    }}
                    className="bg-transparent outline-none border-none flex-1 font-body text-sm"
                    style={{ color: "var(--mt-text)", fontSize: "0.82rem", minWidth: 0 }}
                  />
                  {searchValue && (
                    <button onClick={() => setSearchValue("")} aria-label="Clear">
                      <X size={12} style={{ color: "var(--mt-text-muted)" }} />
                    </button>
                  )}
                </div>

                {(searchOpen || searchValue) && searchValue && (
                  <div className="absolute right-0 top-[calc(100%+8px)] w-[300px] rounded-2xl p-2 z-50"
                    style={{
                      background: mode === "dark" ? "rgba(8,8,12,0.97)" : "rgba(255,255,255,0.97)",
                      border: "1px solid var(--mt-glass-border)",
                      boxShadow: "0 20px 50px -10px rgba(0,0,0,0.5)",
                      backdropFilter: "blur(30px)",
                    }}>
                    {searchResults.length === 0 ? (
                      <div className="px-3 py-3">
                        <p className="font-mono text-[0.58rem] tracking-mt uppercase mb-2 text-center" style={{ color: "var(--mt-text-muted)" }}>You might like:</p>
                        {allProducts.filter(p => !p.coming_soon).slice(0, 3).map(p => (
                          <SearchItem key={p.id} p={p} onSelect={() => { setSearchValue(""); setSearchOpen(false); }} />
                        ))}
                      </div>
                    ) : (
                      <>
                        {searchResults.map(p => (
                          <SearchItem key={p.id} p={p} onSelect={() => { setSearchValue(""); setSearchOpen(false); }} />
                        ))}
                        <Link to="/shop" onClick={() => { setSearchValue(""); setSearchOpen(false); }}
                          className="block text-center px-3 py-2 font-mono text-[0.6rem] tracking-mt uppercase"
                          style={{ color: "var(--mt-accent-blue)", borderTop: "1px solid var(--mt-glass-border)", marginTop: 4 }}>
                          View all in Shop →
                        </Link>
                      </>
                    )}
                  </div>
                )}
              </div>

              <button type="button" onClick={openCart} className="relative p-2.5 rounded-full transition-transform hover:-translate-y-0.5" aria-label="Cart">
                <ShoppingBag size={18} />
                {cartCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[17px] h-[17px] px-0.5 rounded-full flex items-center justify-center font-mono text-[0.5rem]"
                    style={{ background: "var(--mt-text)", color: "var(--mt-bg)" }}>
                    {cartCount}
                  </span>
                )}
              </button>

              <Link to="/profile" className="rounded-full w-9 h-9 flex items-center justify-center transition-transform hover:-translate-y-0.5"
                style={{ border: "1px solid var(--mt-glass-border)" }} aria-label="Profile">
                <User size={15} />
              </Link>

              <button type="button" onClick={toggleMode} className="p-2.5 rounded-full transition-transform hover:-translate-y-0.5" aria-label="Toggle theme">
                {mode === "dark" ? <Sun size={17} /> : <Moon size={17} />}
              </button>

              <Link to="/wishlist" className="p-2.5 rounded-full transition-transform hover:-translate-y-0.5" aria-label="Wishlist">
                <Heart size={17} />
              </Link>
            </div>
          </div>
        </div>

        {/* Mobile + tablet search bar — slides down */}
        {searchOpen && (
          <div className="lg:hidden px-4 py-2"
            style={{
              background: barBg,
              backdropFilter: "blur(40px)",
              borderBottom: "1px solid var(--mt-glass-border)",
            }}>
            <div className="flex items-center gap-2 rounded-full px-4 h-10"
              style={{ border: "1px solid rgba(0,170,255,0.4)", background: "var(--mt-glass-bg)" }}>
              <Search size={14} style={{ color: "var(--mt-text-muted)" }} />
              <input
                autoFocus
                type="text"
                placeholder="Search threads..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && searchResults.length > 0) {
                    navigate(`/product/${searchResults[0].id}`);
                    setSearchValue(""); setSearchOpen(false);
                  }
                }}
                className="flex-1 bg-transparent outline-none border-none font-body"
                style={{ color: "var(--mt-text)", fontSize: "1rem" }}
              />
              <button onClick={() => { setSearchOpen(false); setSearchValue(""); }}><X size={14} style={{ color: "var(--mt-text-muted)" }} /></button>
            </div>
            {searchValue && searchResults.length > 0 && (
              <div className="mt-2 rounded-2xl overflow-hidden" style={{ border: "1px solid var(--mt-glass-border)", background: mode === "dark" ? "rgba(8,8,12,0.97)" : "rgba(255,255,255,0.97)" }}>
                {searchResults.slice(0, 4).map(p => (
                  <SearchItem key={p.id} p={p} onSelect={() => { setSearchValue(""); setSearchOpen(false); }} />
                ))}
              </div>
            )}
          </div>
        )}
      </header>

      {/* ── MOBILE FULL-SCREEN MENU ──────────────────────────────── */}
      {menuOpen && (
        <div className="md:hidden fixed inset-0 z-[60]" style={{ background: mode === "dark" ? "rgba(4,4,8,0.97)" : "rgba(250,250,250,0.97)", backdropFilter: "blur(40px)" }}>
          <div className="flex flex-col h-full px-6">
            {/* Header */}
            <div className="flex items-center justify-between h-[68px] flex-shrink-0">
              <img src="/logo.png" alt="Million Threads" style={{ height: 44, width: 44, objectFit: "contain", filter: mode === "dark" ? "none" : "invert(1)" }} />
              <button type="button" onClick={() => setMenuOpen(false)} className="p-2 rounded-full" style={{ border: "1px solid var(--mt-glass-border)" }}>
                <X size={18} />
              </button>
            </div>

            {/* Nav links */}
            <nav className="flex flex-col gap-1 mt-4 flex-1">
              {navItems.map((it) => (
                <Link
                  key={it.to}
                  to={it.to}
                  onClick={() => setMenuOpen(false)}
                  className="font-display font-light py-4 text-[2.2rem] leading-tight border-b"
                  style={{ color: "var(--mt-text)", borderColor: "var(--mt-glass-border)", letterSpacing: "-0.02em" }}
                >
                  {it.label}
                </Link>
              ))}

              {/* Extra links */}
              <div className="flex gap-4 mt-6 flex-wrap">
                <Link to="/wishlist" onClick={() => setMenuOpen(false)} className="flex items-center gap-2 font-mono text-[0.65rem] tracking-mt uppercase" style={{ color: "var(--mt-text-muted)" }}>
                  <Heart size={14} /> Wishlist
                </Link>
                <Link to="/profile" onClick={() => setMenuOpen(false)} className="flex items-center gap-2 font-mono text-[0.65rem] tracking-mt uppercase" style={{ color: "var(--mt-text-muted)" }}>
                  <User size={14} /> Profile
                </Link>
                <button type="button" onClick={() => { toggleMode(); }} className="flex items-center gap-2 font-mono text-[0.65rem] tracking-mt uppercase" style={{ color: "var(--mt-text-muted)" }}>
                  {mode === "dark" ? <Sun size={14} /> : <Moon size={14} />}
                  {mode === "dark" ? "Light" : "Dark"} mode
                </button>
              </div>
            </nav>

            {/* Bottom */}
            <div className="py-6 flex-shrink-0">
              <button type="button" onClick={() => { openCart(); setMenuOpen(false); }}
                className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl font-mono text-[0.72rem] tracking-mt uppercase"
                style={{ border: "1px solid var(--mt-glass-border)", color: "var(--mt-text)" }}>
                <ShoppingBag size={16} />
                View Bag {cartCount > 0 && `(${cartCount})`}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function SearchItem({ p, onSelect }) {
  return (
    <Link to={`/product/${p.id}`} onClick={onSelect}
      className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors"
      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--mt-glass-bg)")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
      <div className="w-9 h-11 rounded-lg flex-shrink-0 overflow-hidden" style={{ background: "linear-gradient(135deg,#1c1c22,#000)" }}>
        {p.image ? <img src={p.image} alt={p.name} className="w-full h-full object-cover" /> : null}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-body text-sm truncate" style={{ color: "var(--mt-text)" }}>{p.name}</p>
        <p className="font-mono text-[0.58rem] tracking-mt uppercase" style={{ color: "var(--mt-text-muted)" }}>
          ₹{Number(p.sale_price || p.price).toLocaleString("en-IN")}
        </p>
      </div>
    </Link>
  );
}
